import Axios from 'axios';
import http from '../../../http-common';
import { useSelector, shallowEqual } from 'react-redux';
import { React, useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react';
import {
  validatePhone,
  validateWebsite,
  validateFollowers,
} from './validation.js';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getMe, updateUserProfile } from '../../../api/users.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faCheck, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import withChunkReload from '../../../utils/withChunkReload.js';

const LocationSelects = lazy(() => withChunkReload(() => import('./LocationSelects.js')));

const inputClass =
  'tw-h-10 tw-w-full tw-rounded-lg tw-border tw-border-white/20 tw-bg-[#190033] tw-px-4 tw-text-sm tw-text-white placeholder:tw-text-white/30 focus:tw-border-[#712CB0] focus:tw-outline-none tw-transition-colors';

// Fields this form edits. Sending only these keeps a stale Settings tab from
// overwriting newer profile data (photo_albums, video_gallery, ...) with the
// getMe() snapshot it loaded. picture goes only when a new avatar was uploaded.
const SETTINGS_FIELDS = ['username', 'phone', 'website', 'city', 'province', 'country', 'specialization', 'newsletter_subs'];

export const settingsPayload = (data, newPicture) => {
  const payload = {};
  SETTINGS_FIELDS.forEach((field) => {
    if (data[field] !== undefined) payload[field] = data[field];
  });
  if (newPicture) payload.picture = newPicture;
  return payload;
};

export default function Profile() {
  const { t } = useTranslation();

  const [filename, setFilename] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const [userProfileData, setUserProfileData] = useState({
    email: '',
    username: '',
    phone: '',
    website: '',
    city: '',
    province: '',
    country: '',
    role: '',
    specialization: '',
    picture: '',
    newsletter_subs: '',
  });

  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [originalUsername, setOriginalUsername] = useState('');
  const usernameDebounceRef = useRef(null);

  const [validationErrors, setValidationErrors] = useState({});

  const user = useSelector((state) => state.user, shallowEqual);
  const userId = user?.id || '0';

  useEffect(() => {
    if (userId === '0') return;
    getMe()
      .then((data) => {
        setUserProfileData((prev) => ({ ...prev, ...data }));
        setOriginalUsername(data.username || '');
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || t('Error loading profile data'));
      });
  }, [userId, t]);

  useEffect(() => {
    const activePic = filename || userProfileData.picture;
    if (activePic) {
      const imageUrl = activePic.startsWith('https')
        ? activePic
        : `${process.env.REACT_APP_AWS_URL}/${activePic}`;
      setBackgroundImageUrl(imageUrl);
    }
  }, [userProfileData.picture, filename]);

  async function fileSelected(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
      let formData = new FormData();
      formData.append('file', file);
      try {
        // http-common adds the token and silently refreshes an expired one.
        const response = await http.post(
          '/users/uploadUserAvatar',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (response.data?.key) {
          setFilename(response.data.key);
          setDisabled(false);
        }
      } catch (e) {
        toast.error(t('Failed to upload image'));
      }
    } else {
      toast.error(t('File must be an image (jpeg, jpg or png)'));
    }
  }

  const checkUsername = useCallback(async (value) => {
    const val = value.toLowerCase().trim();
    if (!val || val === originalUsername) { setUsernameStatus(null); return; }
    const usernameRegex = /^[a-z0-9_]{3,20}$/;
    if (!usernameRegex.test(val)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    try {
      const { data } = await Axios.get(`${process.env.REACT_APP_BACKEND_URL}/users/check-username/${val}`);
      setUsernameStatus(data.available ? 'available' : 'taken');
    } catch {
      setUsernameStatus(null);
    }
  }, [originalUsername]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    let error = '';

    if (name === 'phone') {
      error = validatePhone(value) ? '' : t('Invalid phone number');
    } else if (name.includes('url') || name === 'website') {
      error = validateWebsite(value) ? '' : t('Invalid URL');
    } else if (name.includes('followers') || name.includes('subs')) {
      error = validateFollowers(value) ? '' : t('Invalid number');
    }

    setValidationErrors((prev) => ({ ...prev, [name]: error }));

    setUserProfileData((prevState) => {
      const newState = { ...prevState, [name]: value };
      if (name === 'country') {
        newState.province = '';
        newState.city = '';
      } else if (name === 'province') {
        newState.city = '';
      }
      return newState;
    });

    setDisabled(false);

    if (name === 'username') {
      clearTimeout(usernameDebounceRef.current);
      usernameDebounceRef.current = setTimeout(() => checkUsername(value), 500);
    }
  };

  async function saveUserProfile() {
    if (Object.values(validationErrors).some((error) => error)) {
      toast.warning(t('Please fix the validation errors before saving.'));
      return;
    }
    if (usernameStatus === 'taken' || usernameStatus === 'invalid') {
      toast.warning(t('Please fix your handle before saving.'));
      return;
    }

    setIsUploading(true);
    try {
      await updateUserProfile(userId, settingsPayload(userProfileData, filename));
      toast.success(t('Updated profile successfully!'));
      setDisabled(true);
    } catch (err) {
      toast.error(err.response?.data?.message || t('An error occurred'));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-6">
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6">
        {/* Left column - form fields */}
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-w-1/2">
          <input
            type="text"
            name="email"
            placeholder={t("Email")}
            value={userProfileData.email}
            onChange={handleProfileChange}
            className={`${inputClass} tw-opacity-50 tw-cursor-not-allowed`}
            disabled
          />

          <div>
            <div className="tw-relative">
              <span className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-[#712CB0] tw-font-bold tw-text-sm tw-pointer-events-none">@</span>
              <input
                type="text"
                name="username"
                placeholder={t('yourhandle')}
                value={userProfileData.username || ''}
                onChange={handleProfileChange}
                className={`${inputClass} tw-pl-7 ${
                  usernameStatus === 'taken' || usernameStatus === 'invalid' ? 'tw-border-red-500' :
                  usernameStatus === 'available' ? 'tw-border-green-500' : ''
                }`}
              />
              {usernameStatus && (
                <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2">
                  {usernameStatus === 'checking' && <FontAwesomeIcon icon={faSpinner} className="tw-text-[#712CB0] tw-animate-spin tw-text-xs" />}
                  {usernameStatus === 'available' && <FontAwesomeIcon icon={faCheck} className="tw-text-green-500 tw-text-xs" />}
                  {(usernameStatus === 'taken' || usernameStatus === 'invalid') && <FontAwesomeIcon icon={faXmark} className="tw-text-red-500 tw-text-xs" />}
                </span>
              )}
            </div>
            {usernameStatus === 'taken' && <p className="tw-mt-1 tw-text-xs tw-text-red-400">{t('That handle is already taken.')}</p>}
            {usernameStatus === 'invalid' && <p className="tw-mt-1 tw-text-xs tw-text-red-400">{t('3-20 characters: letters, numbers, underscores only.')}</p>}
            {usernameStatus === 'available' && <p className="tw-mt-1 tw-text-xs tw-text-green-500">{t('Handle is available!')}</p>}
          </div>

          <div>
            <input
              type="text"
              name="phone"
              placeholder={t('Phone')}
              value={userProfileData.phone}
              onChange={handleProfileChange}
              className={inputClass}
            />
            {validationErrors.phone && (
              <p className="tw-mt-1 tw-text-xs tw-text-red-400">{validationErrors.phone}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              name="website"
              placeholder={t('Website')}
              value={userProfileData.website}
              onChange={handleProfileChange}
              className={inputClass}
            />
            {validationErrors.website && (
              <p className="tw-mt-1 tw-text-xs tw-text-red-400">{validationErrors.website}</p>
            )}
          </div>

          <Suspense
            fallback={[0, 1, 2].map(i => (
              <div key={i} className="tw-h-10 tw-w-full tw-rounded-lg tw-border tw-border-white/20 tw-bg-[#190033] tw-opacity-40" />
            ))}
          >
            <LocationSelects
              userProfileData={userProfileData}
              handleProfileChange={handleProfileChange}
              validationErrors={validationErrors}
            />
          </Suspense>

          <div>
            <input
              type="text"
              name="newsletter_subs"
              placeholder={t('Newsletter Subscribers')}
              value={userProfileData.newsletter_subs || ''}
              onChange={handleProfileChange}
              className={inputClass}
            />
            {validationErrors.newsletter_subs && (
              <p className="tw-mt-1 tw-text-xs tw-text-red-400">{validationErrors.newsletter_subs}</p>
            )}
          </div>
        </div>

        {/* Right column - avatar + specialization + save */}
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 lg:tw-w-1/2">
          <label htmlFor="profileImageUpload" className="tw-cursor-pointer tw-group">
            <div
              className="tw-relative tw-h-32 tw-w-32 tw-rounded-xl tw-bg-cover tw-bg-center tw-bg-white/10 tw-border tw-border-white/20 tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
              style={{ backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none' }}
              title={t('Click to change image')}
            >
              {!backgroundImageUrl && (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                  <FontAwesomeIcon icon={faCamera} className="tw-text-white/30 tw-text-2xl" />
                  <span className="tw-text-white/30 tw-text-xs">{t('Upload')}</span>
                </div>
              )}
              <div className="tw-absolute tw-inset-0 tw-bg-black/40 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center">
                <FontAwesomeIcon icon={faCamera} className="tw-text-white tw-text-xl" />
              </div>
            </div>
          </label>
          <input
            id="profileImageUpload"
            type="file"
            onChange={fileSelected}
            accept="image/*"
            className="tw-hidden"
          />

          {userProfileData.role === 'Industry Professional' && (
            <select
              name="specialization"
              value={userProfileData.specialization}
              onChange={handleProfileChange}
              className="tw-h-10 tw-w-full tw-max-w-xs tw-rounded-lg tw-border tw-border-white/20 tw-bg-[#190033] tw-px-4 tw-text-sm tw-text-white focus:tw-border-[#712CB0] focus:tw-outline-none tw-transition-colors"
            >
              <option value="">{t('Select Specialization')}</option>
              <option value="Distributor">{t('Distributor')}</option>
              <option value="Sales Agent">{t('Sales Agent')}</option>
              <option value="Film Festival">{t('Film Festival')}</option>
              <option value="Producer">{t('Producer')}</option>
              <option value="Writer">{t('Writer')}</option>
              <option value="Cinematographer">{t('Cinematographer')}</option>
              <option value="Investor">{t('Investor')}</option>
            </select>
          )}

          <button
            disabled={disabled || isUploading}
            onClick={saveUserProfile}
            className="tw-rounded-xl tw-bg-[#712CB0] hover:tw-bg-[#8B3FD9] tw-px-8 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition-all active:tw-scale-95 disabled:tw-bg-white/10 disabled:tw-text-white/30 disabled:tw-cursor-not-allowed tw-border-0 tw-cursor-pointer"
          >
            {isUploading ? (
              <span className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-white/30 tw-border-t-white tw-inline-block" />
                {t('Uploading...')}
              </span>
            ) : (
              t('Save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
